"use client";

import { IconExclamationCircle } from "central-icons/IconExclamationCircle";
import { useId, useState } from "react";
import Comparison from "@/components/preview/comparison";
import { cn } from "@/lib/utils";
import DemoInput from "../demo-input";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERROR_MESSAGE = "Enter a valid email address";

type Signals = "all" | "border";

function Field({
  invalid,
  onChange,
  signals,
  value,
}: {
  invalid: boolean;
  onChange: (value: string) => void;
  signals: Signals;
  value: string;
}) {
  const inputId = useId();
  const errorId = useId();
  const showError = signals === "all" && invalid;

  return (
    <div className="flex w-full flex-col gap-2">
      <label className="text-gray-1000 text-sm" htmlFor={inputId}>
        Email address
      </label>

      <div className="relative">
        <DemoInput
          aria-describedby={showError ? errorId : undefined}
          aria-invalid={showError ? true : undefined}
          autoComplete="email"
          className={cn(
            showError && "pr-10",
            invalid && "outline-red-500 ring-red-500/25"
          )}
          id={inputId}
          onChange={(event) => onChange(event.target.value)}
          type="email"
          value={value}
        />
        {showError && (
          <IconExclamationCircle className="pointer-events-none absolute top-1/2 right-3 size-4.5 -translate-y-1/2 text-red-500" />
        )}
      </div>

      <p
        className="flex min-h-5 items-start gap-1 text-pretty text-red-500 text-sm leading-5"
        id={errorId}
      >
        {showError && (
          <>
            <span className="flex size-5 items-center justify-center">
              <IconExclamationCircle className="size-4 shrink-0" />
            </span>
            {ERROR_MESSAGE}
          </>
        )}
      </p>
    </div>
  );
}

export default function AccessibilityColorAloneDemo() {
  const [email, setEmail] = useState("hello@");
  const invalid = !EMAIL_PATTERN.test(email);

  return (
    <Comparison
      bad={
        <Field
          invalid={invalid}
          onChange={setEmail}
          signals="border"
          value={email}
        />
      }
      good={
        <Field
          invalid={invalid}
          onChange={setEmail}
          signals="all"
          value={email}
        />
      }
      panelClassName="px-6 py-8"
      stackBelow="sm"
    />
  );
}
